import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'

import { Details, Term, Definition } from '../../common/Details'
import { normalizeVuln } from '@/api/normalizers'
import {
  SummaryWithFreemium,
  // VulnProductsWithFreemium,
  VulnTypeWithFreemium,
  MitigationWithFreemium,
  DescriptionWithFreemium,
  AnalysisWithFreemium,
  WorkArounds,
  MalwareWithFreemium,
  ActorsWithFreemium,
  ToolsList,
  EPSSWithFreemium,
  CISADates,
  MitigationRow,
  ExploitationRow
} from './ComponentsWithFreemium'
import { SeveritySummary } from './Severity'
import { CVSSSummary } from './CVSSSummary'
import VulnTimeline from './VulnTimeline'

export const VulnerabilityDetails = ({ cveData }) => {
  const cve = useMemo(() => {
    if (!cveData) return null
    return normalizeVuln(cveData)
  }, [cveData])

  const {
    analysis,
    associatedActors,
    associatedMalware,
    associatedTools,
    available_mitigation: mitigation,
    cisaKnownExploited,
    days_to_patch: daysToPatch,
    disclosureDate,
    description,
    epss,
    exploitation_consequence: exploitConsequence,
    exploitation_state: exploitRating,
    exploitation_vectors: exploitVectors,
    exploits,
    executive_summary: summary,
    mitigation_notes: mitigationDetails,
    observed_in_the_wild: inTheWild,
    risk,
    cwe_details: vulnType,
    was_zero_day: zeroDay,
    workarounds_list: workarounds,
    common_vulnerability_scores: vulnScores,
    publishedDate,
    is_predicted: isPredicted
  } = cve || {}

  if (!cveData) return null

  return (
    <Details>
      { !isEmpty(summary) && (
        <>
          <Term>Executive Summary</Term>
          <Definition>
            <SummaryWithFreemium summary={summary} />
          </Definition>
        </>
      )}

      <Term>Severity</Term>
      <Definition>
        <SeveritySummary
          exploitRating={exploitRating}
          risk={risk}
          inTheWild={inTheWild}
          zeroDay={zeroDay}
          isPredicted={isPredicted}
        />
      </Definition>

      { !isEmpty(associatedActors) && (
        <>
          <Term>Associated Actors</Term>
          <Definition>
            <ActorsWithFreemium actors={associatedActors} />
          </Definition>
        </>
      )}

      { !isEmpty(associatedMalware) && (
        <>
          <Term>Associated Malware</Term>
          <Definition>
            <MalwareWithFreemium malware={associatedMalware} />
          </Definition>
        </>
      )}

      { !isEmpty(associatedTools) && (
        <>
          <Term>Associated Tools</Term>
          <Definition>
            <ToolsList tools={associatedTools} />
          </Definition>
        </>
      )}

      <Term>Vulnerability Timeline</Term>
      <Definition marginTop='15px' marginBottom='35px' display="inline-grid">
        <VulnTimeline
          cisaKnownExploited={cisaKnownExploited}
          disclosureDate={disclosureDate}
          exploits={exploits}
          publishedDate={publishedDate}
        />
      </Definition>

      { !isEmpty(description) && (
        <>
          <Term>Description</Term>
          <Definition>
            <DescriptionWithFreemium description={description} />
          </Definition>
        </>
      )}

      { !isEmpty(analysis) && (
        <>
          <Term>Analysis</Term>
          <Definition>
            <AnalysisWithFreemium analysis={analysis} />
          </Definition>
        </>
      )}

      { !isEmpty(vulnScores) && (
        <>
          <Term>CVSS</Term>
          <Definition>
            <CVSSSummary
              exploitRating={exploitRating}
              risk={risk}
              inTheWild={inTheWild}
              zeroDay={zeroDay}
              vulnScores={vulnScores}
            />
          </Definition>
        </>
      )}

      { !isEmpty(cisaKnownExploited) && (
        <>
          <Term>CISA Known Exploited Vulnerabilities</Term>
          <Definition>
            <CISADates cisaKnownExploited={cisaKnownExploited} />
          </Definition>
        </>
      )}
      { !isEmpty(epss) && (
        <>
          <Term>EPSS</Term>
          <Definition>
            <EPSSWithFreemium epss={epss} />
          </Definition>
        </>
      )}
      { !isEmpty(vulnType) && (
        <>
          <Term>CWE</Term>
          <Definition>
            <VulnTypeWithFreemium vulnType={vulnType} />
          </Definition>
        </>
      )}

      <>
        <Term>Mitigation</Term>
        <Definition>
          <MitigationRow
            mitigation={mitigation}
            disclosureDate={disclosureDate}
            daysToPatch={daysToPatch}
          />
        </Definition>
      </>

      {
        exploitConsequence && exploitVectors &&
      <>
        <Term>Exploitation</Term>
        <Definition>
          <ExploitationRow
            exploitConsequence={exploitConsequence}
            exploitVectors={exploitVectors}
          />
        </Definition>
      </>
      }

      { !isEmpty(workarounds) && (
        <>
          <Term>Workarounds</Term>
          <Definition>
            <WorkArounds workarounds={workarounds} />
          </Definition>
        </>
      )}

      { !isEmpty(mitigationDetails) && (
        <>
          <Term>Mitigation</Term>
          <Definition>
            <MitigationWithFreemium mitigation={mitigationDetails} />
          </Definition>
        </>
      )}
    </Details>
  )
}

VulnerabilityDetails.propTypes = {
  id: PropTypes.string,
  cveData: PropTypes.object,
}

export default React.memo(VulnerabilityDetails)
